/**
 * localKdsOps — offline-first Kitchen Display Screen operations backed by
 * local SQLite. Kept separate from localOrderOps.ts (already large and
 * order-item-centric) since KDS reads join kitchen_tickets against
 * orders/outlet_tables/users/menu_items in a distinct shape.
 *
 * Reads are scoped to a single department (the KDS UI requires a department
 * to be selected before showing any board content) and exclude SERVED
 * tickets from the board itself — served tickets only feed the KPI bar.
 */

import {
  KdsDepartmentOption,
  KdsKpis,
  KdsTicket,
  KdsTicketItem,
  KDS_DELAYED_THRESHOLD_MINUTES,
  KDS_STATUS_ORDER,
  KitchenTicketStatus,
} from '@/renderer/types/kds';
import { ApiResponse } from '@shared/types';

import { db } from './database';
import { psGet, psGetAll } from './ipcBridge';
import {
  genUUID,
  LocalOrderContext,
  nowIso,
  todayDate,
  toNumber,
} from './localOrderOps';

/** Timestamp column stamped when a ticket enters a given status. */
const STATUS_TIMESTAMP_COLUMN: Partial<Record<KitchenTicketStatus, string>> = {
  IN_PREP: 'in_prep_at',
  QC: 'qc_at',
  READY: 'ready_at',
  SERVED: 'served_at',
};

/**
 * Active departments for the KDS department picker, ordered the same way the
 * sections/departments management screen orders them.
 *
 * Deliberately NOT filtered by `kot_enabled` — that flag only controls
 * whether a printed paper KOT is generated for the department (see
 * `localGetDepartmentKotEnabled`); it has no bearing on whether
 * `kitchen_tickets` rows exist for it. Both `confirm_order_kot` (online) and
 * `localConfirmOrderKot` (offline) create a ticket for every distinct
 * department found among confirmed items regardless of `kot_enabled`, so
 * filtering the picker by it hid departments that legitimately receive KDS
 * tickets.
 */
export async function localFetchDepartmentsForKds(
  outletId: string,
): Promise<ApiResponse<KdsDepartmentOption[]>> {
  try {
    const rows = await psGetAll<any>(
      `SELECT id, name FROM departments
       WHERE outlet_id = ? AND status = 'active'
       ORDER BY display_order ASC, name ASC`,
      [outletId],
    );
    return {
      success: true,
      data: rows.map((r) => ({ id: r.id, name: r.name || '' })),
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch departments',
      data: [],
    };
  }
}

/**
 * Active (not yet served) kitchen tickets for a department, oldest first,
 * with their item lines. Callers group the result by `status` into the
 * board's 4 columns.
 */
export async function localFetchKdsBoard(
  departmentId: string,
  ctx: LocalOrderContext,
): Promise<ApiResponse<KdsTicket[]>> {
  try {
    const ticketRows = await psGetAll<any>(
      `SELECT
         kt.id, kt.order_id, kt.kot_id, kt.department_id, kt.status,
         kt.created_at, kt.in_prep_at, kt.qc_at, kt.ready_at, kt.served_at,
         o.bill_number, o.mode, o.table_id,
         ot.name                              AS table_name,
         (u.first_name || ' ' || u.last_name)  AS waiter_name
       FROM kitchen_tickets kt
       JOIN orders o ON o.id = kt.order_id
       LEFT JOIN outlet_tables ot ON CAST(o.table_id AS TEXT) = ot.id
       LEFT JOIN users u ON o.waiter_id = u.id
       WHERE kt.outlet_id = ? AND kt.department_id = ? AND kt.status != 'SERVED'
       ORDER BY kt.created_at ASC`,
      [ctx.outletId, departmentId],
    );

    // Independent reads (no shared transaction) — safe to run concurrently.
    const tickets: KdsTicket[] = await Promise.all(
      ticketRows.map(async (row): Promise<KdsTicket> => {
        const itemRows = await psGetAll<any>(
          `SELECT oi.id, mi.name AS item_name, oi.quantity, oi.notes
           FROM order_items oi
           JOIN menu_items mi ON mi.id = oi.menu_item_id
           WHERE oi.order_id = ? AND oi.kot_id = ? AND mi.department_id = ?
           ORDER BY mi.name ASC`,
          [row.order_id, row.kot_id, departmentId],
        );
        const items: KdsTicketItem[] = itemRows.map((i) => ({
          id: i.id,
          name: i.item_name || '',
          quantity: toNumber(i.quantity),
          notes: i.notes ?? null,
        }));

        return {
          id: row.id,
          order_id: row.order_id,
          kot_id: toNumber(row.kot_id),
          department_id: row.department_id ?? null,
          status: row.status,
          bill_number: toNumber(row.bill_number),
          mode: row.mode,
          table_name: row.table_name ?? null,
          waiter_name: row.waiter_name ?? null,
          created_at: row.created_at,
          in_prep_at: row.in_prep_at ?? null,
          qc_at: row.qc_at ?? null,
          ready_at: row.ready_at ?? null,
          served_at: row.served_at ?? null,
          items,
        };
      }),
    );

    return { success: true, data: tickets };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch KDS board',
      data: [],
    };
  }
}

/**
 * KPI bar figures for a department: Active (unserved), Completed (served
 * today), Delayed (unserved, past the delay threshold), Average Time
 * (minutes from creation to served, over today's served tickets).
 *
 * Average Time is computed in JS from raw ISO timestamps rather than a SQL
 * date function (e.g. julianday()), since availability of SQLite date
 * functions in PowerSync's embedded build is unconfirmed — plain string
 * comparison (for the delayed cutoff) and JS Date arithmetic (for the
 * average) both avoid that dependency entirely.
 */
export async function localFetchKdsKpis(
  departmentId: string,
  ctx: LocalOrderContext,
): Promise<ApiResponse<KdsKpis>> {
  try {
    const today = todayDate();
    const delayedCutoffIso = new Date(
      Date.now() - KDS_DELAYED_THRESHOLD_MINUTES * 60_000,
    ).toISOString();

    const activeRow = await psGet<any>(
      `SELECT COUNT(*) AS cnt FROM kitchen_tickets
       WHERE outlet_id = ? AND department_id = ? AND status != 'SERVED'`,
      [ctx.outletId, departmentId],
    );

    const delayedRow = await psGet<any>(
      `SELECT COUNT(*) AS cnt FROM kitchen_tickets
       WHERE outlet_id = ? AND department_id = ? AND status != 'SERVED'
         AND created_at <= ?`,
      [ctx.outletId, departmentId, delayedCutoffIso],
    );

    const completedRow = await psGet<any>(
      `SELECT COUNT(*) AS cnt FROM kitchen_tickets
       WHERE outlet_id = ? AND department_id = ? AND status = 'SERVED' AND order_date = ?`,
      [ctx.outletId, departmentId, today],
    );

    const servedTodayRows = await psGetAll<any>(
      `SELECT created_at, served_at FROM kitchen_tickets
       WHERE outlet_id = ? AND department_id = ? AND status = 'SERVED' AND order_date = ?`,
      [ctx.outletId, departmentId, today],
    );

    let averageTimeMinutes = 0;
    if (servedTodayRows.length > 0) {
      const totalMinutes = servedTodayRows.reduce((sum, r) => {
        if (!r.served_at || !r.created_at) return sum;
        const durationMs =
          new Date(r.served_at).getTime() - new Date(r.created_at).getTime();
        return sum + Math.max(durationMs, 0) / 60_000;
      }, 0);
      averageTimeMinutes = Math.round(totalMinutes / servedTodayRows.length);
    }

    return {
      success: true,
      data: {
        active_orders: toNumber(activeRow?.cnt),
        average_time_minutes: averageTimeMinutes,
        completed_orders: toNumber(completedRow?.cnt),
        delayed_orders: toNumber(delayedRow?.cnt),
      },
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch KDS KPIs',
      data: {
        active_orders: 0,
        average_time_minutes: 0,
        completed_orders: 0,
        delayed_orders: 0,
      },
    };
  }
}

/**
 * Move a kitchen ticket forward to a new status, stamping the matching stage
 * timestamp and recording the transition in the append-only history table.
 * Atomic via db.writeTransaction — mirrors the pattern in
 * localOrderOps.ts's localGetOrCreateTableOrder.
 */
export async function localTransitionKitchenTicket(
  ticketId: string,
  toStatus: KitchenTicketStatus,
  ctx: LocalOrderContext,
): Promise<ApiResponse<void>> {
  try {
    await db.writeTransaction(async (tx) => {
      const row = await tx.get<any>(
        `SELECT status FROM kitchen_tickets WHERE id = ? LIMIT 1`,
        [ticketId],
      );
      if (!row) {
        throw new Error(
          'Ticket not found — it may have already been synced away',
        );
      }

      const fromStatus = row.status as KitchenTicketStatus;
      const fromIndex = KDS_STATUS_ORDER.indexOf(fromStatus);
      const toIndex = KDS_STATUS_ORDER.indexOf(toStatus);
      if (toIndex <= fromIndex) {
        throw new Error(
          `Cannot move a ticket from ${fromStatus} back to ${toStatus}`,
        );
      }

      const now = nowIso();
      const tsColumn = STATUS_TIMESTAMP_COLUMN[toStatus];
      const params: unknown[] = [toStatus, ctx.userId, now];
      if (tsColumn) params.push(now);
      params.push(ticketId);

      await tx.execute(
        `UPDATE kitchen_tickets
         SET status = ?, updated_by = ?, updated_at = ?${tsColumn ? `, ${tsColumn} = ?` : ''}
         WHERE id = ?`,
        params,
      );

      await tx.execute(
        `INSERT INTO kitchen_ticket_status_history
           (id, ticket_id, outlet_id, order_date, from_status, to_status, changed_by, changed_at)
         SELECT ?, id, outlet_id, order_date, ?, ?, ?, ?
         FROM kitchen_tickets WHERE id = ?`,
        [genUUID(), fromStatus, toStatus, ctx.userId, now, ticketId],
      );
    });

    return { success: true, data: undefined };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err.message : 'Failed to update ticket status',
      data: undefined,
    };
  }
}
